export type MilestoneId = 
  | 'first_chat'
  | 'first_space'
  | 'first_source'
  | 'explore_library'
  | 'try_ai_tool'
  | 'complete_brain_workout';

export interface Milestone {
  id: MilestoneId;
  title: string;
  description: string;
  xpReward: number;
  unlocksFeature?: 'dashboard' | 'library' | 'tools' | 'advanced';
  celebrationMessage: string;
  icon: string;
}

export interface OnboardingProgress {
  userId: string;
  completedMilestones: MilestoneId[];
  currentStep: number;
  hasSeenWelcome: boolean;
  hasSeenChatTour: boolean;
  hasSeenLibraryTour: boolean;
  hasSeenToolsTour: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface FeatureUnlock {
  feature: string;
  isUnlocked: boolean;
  requiredMilestone?: MilestoneId;
  unlockedAt?: Date;
}

export const MILESTONES: Milestone[] = [
  {
    id: 'first_chat',
    title: 'First Conversation',
    description: 'Send your first message to the AI',
    xpReward: 50,
    unlocksFeature: 'dashboard',
    celebrationMessage: 'Amazing! You just had your first AI conversation. Your Dashboard is now unlocked!',
    icon: 'MessageSquare'
  },
  {
    id: 'first_space',
    title: 'Knowledge Architect',
    description: 'Create your first Mastermind Space',
    xpReward: 75,
    unlocksFeature: 'library',
    celebrationMessage: 'Brilliant! You created your first knowledge space. The Library is now yours to explore!',
    icon: 'Brain'
  },
  {
    id: 'first_source',
    title: 'Knowledge Hunter',
    description: 'Add your first source (URL, document, or video)',
    xpReward: 50,
    celebrationMessage: 'Nice work! You added your first knowledge source. Keep building your intelligence!',
    icon: 'Upload'
  },
  {
    id: 'explore_library',
    title: 'Library Explorer',
    description: 'Visit your Library and browse your knowledge',
    xpReward: 25,
    unlocksFeature: 'tools',
    celebrationMessage: 'You discovered the Library! AI Tools are now unlocked for you!',
    icon: 'BookOpen'
  },
  {
    id: 'try_ai_tool',
    title: 'Tool Master',
    description: 'Use any AI tool from the toolkit',
    xpReward: 50,
    unlocksFeature: 'advanced',
    celebrationMessage: 'Power user alert! You tried your first AI tool. Advanced features are now available!',
    icon: 'Sparkles'
  },
  {
    id: 'complete_brain_workout',
    title: 'Brain Builder',
    description: 'Complete a Brain Builder workout session',
    xpReward: 100,
    celebrationMessage: 'Your brain just got stronger! Keep training to level up!',
    icon: 'Dumbbell'
  }
];

export const ONBOARDING_TIPS = {
  chat: [
    { id: 'tip_models', text: 'Try different AI models using the selector above - each has unique strengths!', feature: 'model_selector' },
    { id: 'tip_webSearch', text: 'Enable web search to get real-time information in your responses.', feature: 'web_search' },
    { id: 'tip_attachments', text: 'Attach files, images, or paste URLs to analyze content.', feature: 'attachments' },
  ],
  library: [
    { id: 'tip_spaces', text: 'Spaces help you organize knowledge by topic - like folders for your brain!', feature: 'spaces' },
    { id: 'tip_sources', text: 'Import from YouTube, Google Drive, or paste any URL to capture knowledge.', feature: 'sources' },
    { id: 'tip_notes', text: 'Create notes to synthesize and connect your ideas.', feature: 'notes' },
  ],
  tools: [
    { id: 'tip_compare', text: 'Compare responses from multiple AI models side-by-side.', feature: 'compare_models' },
    { id: 'tip_creative', text: 'Generate images with Creative Studio using AI.', feature: 'creative_studio' },
    { id: 'tip_capture', text: 'Quick Capture lets you save ideas instantly from anywhere.', feature: 'quick_capture' },
  ]
};
